/**
 * Hook for managing menu scope user state logic
 * Determines if user is new based on mainMenuOrganization flag
 * Store access requires: NOT new user AND has menus to modify
 *
 * @module hooks/menuScope/useMenuScopeUserState
 *
 * @returns {Object} User state information
 * @returns {boolean} returns.isNewUser - Whether user is new (has mainMenuOrganization flag)
 * @returns {boolean} returns.isUserError - Whether there was an error loading user data
 * @returns {Object|null} returns.user - User data object
 * @returns {boolean} returns.hasMenus - Whether the current store has menus
 * @returns {boolean} returns.isLoadingMenus - Whether menus are being loaded
 * @returns {number} returns.menusCount - Total number of menus in the store
 * @returns {boolean} returns.canAccessStore - Whether user can access Store mode (new logic)
 *
 * @example
 * const { isNewUser, hasMenus, isLoadingMenus, canAccessStore } = useMenuScopeUserState();
 *
 * // Store button logic: disabled = !canAccessStore
 * const storeButtonDisabled = !canAccessStore;
 */

import { useUser } from 'containers/Users';
import { User } from 'containers/Users/types';

import { useStoreHasMenus } from './useStoreHasMenus';

interface UseMenuScopeUserStateReturn {
  isNewUser: boolean;
  isUserError: boolean;
  user: User | null;
  hasMenus: boolean;
  isLoadingMenus: boolean;
  menusCount: number;
  canAccessStore: boolean;
}

export const useMenuScopeUserState = (): UseMenuScopeUserStateReturn => {
  // Get user data to determine if they are new (mainMenuOrganization flag from backend)
  const { data: user, isError: isUserError } = useUser();

  // Get store menus information
  const { hasMenus, isLoading: isLoadingMenus, menusCount } = useStoreHasMenus();

  // Determine if user is new:
  // - User is "new" if they have mainMenuOrganization flag AND no menus exist
  // - Users with mainMenuOrganization but WITH menus can access both modes
  // - Store mode requires: (NOT new user OR has menus) to allow menu modification
  // - Fallback to false (existing user behavior) if user data fails to load
  const isNewUser = isUserError ? false : (user?.mainMenuOrganization ?? false) && !hasMenus;

  // Determine if user can access Store mode based on new logic:
  // - Disable Store if: mainMenuOrganization = true AND hasMenus = false
  // - Enable Store in all other cases:
  //   * mainMenuOrganization = true AND hasMenus = true
  //   * mainMenuOrganization = false AND hasMenus = false
  //   * mainMenuOrganization = false AND hasMenus = true
  const canAccessStore = !((user?.mainMenuOrganization ?? false) && !hasMenus);

  return {
    isNewUser,
    isUserError,
    user: user ?? null,
    hasMenus,
    isLoadingMenus,
    menusCount,
    canAccessStore,
  };
};
